import { defineStore } from 'pinia'
import Network from '@/Network'
import { ListCompanyUserResponse } from '@/Network/Types/Responses/CompanyResponses'
import { UserDataResponse } from '@/Network/Types/Responses/UserResponse'
import { LoginType } from '@/modules/public/Signin/schemas'
import { AuthResponse } from '@/Network/Types/Responses/AuthResponses'

export type AuthState = AuthResponse

export type UserState = {
  users: ListCompanyUserResponse | undefined
  listUsers: { title: string; subtitle: string; value: string }[]
  userData: UserDataResponse | undefined | null
  isLoading: boolean
  searchTerm: string
  accessToken?: string | undefined | null
}

export type Filter = {
  searchTerm?: string
}

export const useUserStore = defineStore('user', {
  state: (): UserState => ({
    users: undefined,
    listUsers: [],
    userData: undefined,
    isLoading: false,
    searchTerm: '',
    accessToken: undefined
  }),
  actions: {
    async fetchUsers(): Promise<void> {
      this.isLoading = true
      try {
        const response = await Network.user.getAllUsers(this.searchTerm)
        this.users = response
        this.listUsers = response.data.map(({ user }) => ({
          title: user.name,
          subtitle: user.email,
          value: String(user.id)
        }))
      } catch (error) {
        console.error('Erro ao buscar usuários:', error)
        this.users = undefined
        this.listUsers = []
        // Relança o `ServiceError` para o caller notificar (antes engolia e a lista
        // aparecia como "nenhum usuário encontrado" sem aviso).
        throw error
      } finally {
        this.isLoading = false
      }
    },

    setSearchTerm(term: string) {
      this.searchTerm = term
    },

    async getUserData(): Promise<void> {
      this.isLoading = true
      try {
        this.userData = await Network.user.getUserData()
      } catch (error) {
        console.error('Erro ao buscar dados do usuário:', error)
        this.userData = null
        throw error
      } finally {
        this.isLoading = false
      }
    },
    async updateUserData(updatedData: Partial<UserDataResponse>): Promise<UserDataResponse> {
      this.isLoading = true
      try {
        const updatedUserData = await Network.user.updateUserData(updatedData)

        this.userData = updatedUserData

        return updatedUserData
      } catch (error) {
        console.error('Erro ao atualizar os dados do usuário:', error)
        throw error
      } finally {
        this.isLoading = false
      }
    },
    async changePassword(password: string, repeatPassword: string): Promise<void> {
      this.isLoading = true
      try {
        await Network.user.changeCompanyUserPassword({ password, repeatPassword })
      } catch (error) {
        console.error('Erro ao alterar a senha:', error)
        throw error
      } finally {
        this.isLoading = false
      }
    },
    async updateAvatar(file: File): Promise<string> {
      const result = await Network.user.updateAvatar(file)
      if (this.userData) {
        this.userData.avatarUrl = result.avatarUrl
      }
      return result.avatarUrl
    },
    async login(data: LoginType): Promise<void> {
      this.isLoading = true
      try {
        const authData: AuthState = await Network.auth.loginUserCompany(data)
        this.accessToken = authData.access_token
        await this.getUserData()
      } catch (error) {
        console.error('Erro ao realizar login:', error)
        throw error
      } finally {
        this.isLoading = false
      }
    }
  }
})
